import { memo, useState } from 'react'
import _ from 'lodash'
import cx from 'classnames'
import { Field } from 'redux-form'
import { TransitionDropList, PopoverImg, Button } from '@vzmi/shp_shared_compo'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import { FormValueSelector } from '../../../Common/forms'
import {
  FormRow,
  RadioGroupInputField,
  CheckboxInputField
} from '../../../Common/Form'
import {
  IconPopoverLabel
} from '../../../Proposal/Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { ATTRIBUTE_DISPLAY_MODE_TYPES } from '../../../../constants/proposalTypes'
import { SELECTED_VALUES_SELECTED, ATTRIBUTE_DISPLAY_MODE_SELECTED } from '../../../../constants/batchProposals'
import StatusPrefixBlock from '../../Common/PrefixBlock/Status'
import DuplicatedPrefixBlock from '../../Common/PrefixBlock/Duplicated'
import ListingCollapseBlock from '../../Common/ListingCollapseBlock'
import CardSectionTitle from '../../Common/CardSectionTitle'
import { CATEGORY_LAYER } from '../helper'
import CurrentPath from './CurrentPath'
import NewPathSelect from './NewPathSelect'
import ProductModels from './ProductModels'
import SpecList from './SpecList'
import styles from './index.css'
import ApplyModeTip from './ApplyModeTip'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from '../../BatchApply.ga'
import { sendI13nClickEvent } from '../../../../../lib/i13nUtil'

const {
  TransitionDropListContainer,
  TransitionDropItem
} = TransitionDropList

export const getSelectedOptions = (selectedOptionMap, listingId) => {
  const selectedOptions = _.flow(
    () => _.get(selectedOptionMap, listingId, {}),
    selectedOptionByLayer => _.map(
      [CATEGORY_LAYER.ONE, CATEGORY_LAYER.TWO, CATEGORY_LAYER.THREE],
      layer => _.get(selectedOptionByLayer, layer, {})
    ),
    options => _.filter(options, option => !_.isEmpty(option))
  )()
  return selectedOptions
}

export const getIsClusterDependOnCatItem = (listing, menuData) => {
  const {
    catItemId,
    struDataAttrClusterId
  } = listing
  const isClusterDependOnCatItem = _.flow(
    () => _.get(menuData, `${CATEGORY_LAYER.THREE}.${catItemId}`, []),
    layerThreeOptions => _.find(layerThreeOptions, { value: struDataAttrClusterId }),
    option => !_.isEmpty(option)
  )()
  return isClusterDependOnCatItem
}

const AttributeRow = memo(props => {
  const {
    index,
    listing = {},
    menuData,
    batchResult,
    isOverviewPage,
    duplicatedMap,
    onRemove,
    readOnly,
    change,
    onLayoutChange,
    proposalType,
    selectedOptionMap,
    optionsMap,
    updateMenuData,
    updateCategoryOptions,
    updateFormListingWithAttributes,
    triggerOptionLoadingState,
    listings,
    i13n,
    changeReduxFromListings
  } = props
  const {
    id,
    subStationId,
    isNewPathSelectable,
    isInConsistentProduct: isInConsistentProductFromListing
  } = listing
  const currentField = `listings[${index}]`
  const fieldId = `${currentField}.attributeDisplayMode`
  const fieldName = `${currentField}.${ATTRIBUTE_DISPLAY_MODE_SELECTED}`
  const currentPaths = [
    `${currentField}.zoneName`,
    `${currentField}.subStationName`
  ]
  // 只有修改頁賣場的商品才會有不一致的情況
  // 進入確認、檢視頁的時候已經重新選擇規格表，所以一率把商品的規格表當作一致
  // 額外這樣判斷是因為在檢視頁只能取得賣場資料從 productStructuredDataList 驗證起來規格表還是不一致的
  // 因為這項功能沒有要修改"賣場的規格表"，所以他如果一開始不一致到檢視頁仍然會不一致
  const isInConsistentProduct = readOnly
    // 確認、檢視頁一率當作商品的規格表一致
    ? false
    : isInConsistentProductFromListing
  // 已選擇的父類、子類及規格選項
  const selectedOptions = isInConsistentProduct
    // 賣場的商品規格表不一致時直接要求重選
    ? []
    : getSelectedOptions(selectedOptionMap, id)
  const selectedOptionValues = _.map(selectedOptions, 'value')
  const categoryPathOptions = _.get(optionsMap, subStationId, [])
  // 需要重選規格表的條件
  const isNeedSelectNewPath = (
    // 未選完整規格表(父類、子類、規格)
    (_.size(selectedOptionValues) !== 3) ||
    // 賣場裡所有商品的規格表編號如果有不一致會要求重選規格表
    // 選擇後 isInConsistentProduct 會變成 false
    isInConsistentProduct
  )
  const isNewPathSelectDisabled = (
    readOnly ||
    // 父類、子類、規格有查不到的情況需要重選，查不到時已選擇的項目會湊不齊
    (
      !isNeedSelectNewPath &&
      // 重選過規格表後賣場會標記成 isNewPathSelectable=true
      // 讓使用者可以再次重新選取
      // (重選後選錯當下 selectedOptionValues 變成已選取 3 項，另外用一個 flag 紀錄是否可以重選)
      !isNewPathSelectable
    )
  )
  // 判斷規格表是不是從目前賣場的子類底下找到的
  const isClusterDependOnCatItem = getIsClusterDependOnCatItem(listing, menuData)

  const [isBatchApplyMode, setIsBatchApplyMode] = useState(false)

  const sendPageview = (action) => {
    const i13nModel = getI13nModel(action)
    sendI13nClickEvent({ i13n, i13nModel })
  }

  const toggleBatchApplyMode = (state, listing = {}, index) => {
    // [確認批次套用]及[取消批次套用] 要取消checkBox勾選
    if (!_.isEmpty(listing)) {
      // 取消規格項目勾選
      const unCheckAttributes = _.map(listing.attributes, item => {
        return _.omit(item, [SELECTED_VALUES_SELECTED])
      })
      listing.attributes = unCheckAttributes
      // 取消商品規格勾選
      const unCheckAttrMode = _.omit(listing, [ATTRIBUTE_DISPLAY_MODE_SELECTED])
      // 修改 redux form 裡的賣場
      change(`listings[${index}]`, unCheckAttrMode)
    }
    // 批次套用、取消批次套用Btn
    setIsBatchApplyMode(!state)
  }
  const onBatchAppply = (state, listing = {}, index) => {
    // 需解構才能取得redux勾選狀態(selectedValues_selected)
    changeReduxFromListings({ ...listing })
    // click GA
    sendPageview({
      type: GA_CLICK_ACTION.BATCH_APPLY_ATTRIBUTE
    })
    // 確認批次套用Btn
    toggleBatchApplyMode(state, listing, index)
  }

  return (
    <>
      {/* 更新成功失敗區塊 */}
      <StatusPrefixBlock
        proposalType={proposalType}
        listing={listing}
        batchResult={batchResult}
        isOverviewPage={isOverviewPage}
      />
      {/* 賣場含有共用商品區塊 */}
      <DuplicatedPrefixBlock
        proposalType={proposalType}
        duplicatedMap={duplicatedMap}
        listing={listing}
      />
      {/* 賣場區塊 */}
      <ListingCollapseBlock
        batchResult={batchResult}
        proposalType={proposalType}
        onRemove={onRemove}
        onCollapseChange={onLayoutChange()}
        listing={listing}
        readOnly={readOnly}
      >
        <CardSectionTitle>
          {/* 賣場規格表 */}
          {' '}
        </CardSectionTitle>
        <FormRow
          label='選擇規格表'
          labelAlign='top'
        >
          <div
            data-role='full-category-path'
            className={styles.fullCategoryPath}
          >
            <CurrentPath paths={currentPaths} />
            <NewPathSelect
              fieldPrefix={currentField}
              listing={listing}
              value={selectedOptionValues}
              options={categoryPathOptions}
              currentPathOptions={selectedOptions}
              change={change}
              updateMenuData={updateMenuData}
              updateCategoryOptions={updateCategoryOptions}
              updateFormListingWithAttributes={updateFormListingWithAttributes}
              triggerOptionLoadingState={triggerOptionLoadingState}
              isNeedSelectNewPath={isNeedSelectNewPath}
              isClusterDependOnCatItem={isClusterDependOnCatItem}
              disabled={isNewPathSelectDisabled}
            />
          </div>
        </FormRow>
        <FormRow
          labelAlign='top'
          label={(
            <IconPopoverLabel
              popoverContent={(
                <PopoverImg
                  url={`${CDN}/img/tooltip_value.png`}
                  width={500}
                  height={400}
                />
              )}
            >
              商品屬性
            </IconPopoverLabel>
          )}
        >
          <ProductModels
            fieldPrefix={currentField}
            listing={listing}
          />
        </FormRow>
        {(!readOnly && listings.length > 1) && ( // 僅在[修改頁] 及 [2筆以上賣場]顯示
          <>
            <FormRow className={styles.divider} />
            <FormRow
              className={cx(
                styles.tableTip,
                { [styles.batchApplyMode]: isBatchApplyMode }
              )}
              labelAlign='top'
              label={(
                <IconPopoverLabel
                  className={styles.labelMode}
                  align='top'
                  popoverContent='將項目內容套用到此次提案之所有賣場上'
                >
                  <Button
                    data-role='batch-apply-btn'
                    className={styles.batchApplyBtn}
                    onClick={() => toggleBatchApplyMode(isBatchApplyMode)}
                    disabled={isBatchApplyMode}
                    buttonType='outline'
                    size='medium'
                  >
                    批次套用
                  </Button>
                </IconPopoverLabel>
              )}
            >
              <ApplyModeTip
                batchApplyMode={isBatchApplyMode}
                toggleBatchApplyMode={toggleBatchApplyMode}
                onBatchAppply={onBatchAppply}
                listing={listing}
                index={index}
              />
            </FormRow>
          </>
        )}
        <FormRow
          labelAlign='top'
          label={(
            <div className={styles.attributeDisplayMode}>
              {
                (!readOnly && isBatchApplyMode) && (
                  <Field
                    className={styles.checkbox}
                    id={fieldId}
                    name={fieldName}
                    component={CheckboxInputField}
                  />
                )
              }
              <IconPopoverLabel
                popoverContent={(
                  <PopoverImg
                    url={`${CDN}/img/tooltip_attributes.png`}
                    width={500}
                    height={610}
                  />
                )}
              >
                商品規格
              </IconPopoverLabel>
            </div>
          )}
        >
          <Field
            name={fieldId}
            data-role='attribute-display-mode-radio'
            component={RadioGroupInputField}
            options={ATTRIBUTE_DISPLAY_MODE_TYPES}
            disabled={readOnly}
          />
        </FormRow>
        <SpecList
          fieldPrefix={currentField}
          // 需要重選規格表或賣場的商品規格表不一致時隱藏規格項目
          // 待重選後顯示規格項目
          visible={!isNeedSelectNewPath}
          disabled={readOnly}
          batchApplyMode={isBatchApplyMode}
        />
      </ListingCollapseBlock>
    </>
  )
})

const AttributeRows = props => {
  const {
    readOnly,
    rowsSize,
    onRemove,
    batchResult,
    duplicatedMap = {},
    isOverviewPage,
    change,
    proposalType,
    menuData,
    selectedOptionMap,
    optionsMap,
    updateMenuData,
    updateCategoryOptions,
    updateFormListingWithAttributes,
    triggerOptionLoadingState,
    i13n,
    changeReduxFromListings
  } = props

  return (
    <FormValueSelector
      // redux proposal form listings
      selector={select => ({
        listings: (select('listings') || []).slice(0, rowsSize)
      })}
    >
      {({ listings = [] }) => {
        return (
          // 刪除賣場轉場容器
          <TransitionDropListContainer>
            {listings.map((listing, index) => {
              const { id } = listing
              return (
                <TransitionDropItem
                  key={id}
                  id={id}
                >
                  {onLayoutChange => (
                    <AttributeRow
                      index={index}
                      listing={listing}
                      batchResult={batchResult}
                      isOverviewPage={isOverviewPage}
                      duplicatedMap={duplicatedMap}
                      onRemove={onRemove}
                      readOnly={readOnly}
                      change={change}
                      onLayoutChange={onLayoutChange}
                      proposalType={proposalType}
                      menuData={menuData}
                      selectedOptionMap={selectedOptionMap}
                      optionsMap={optionsMap}
                      updateMenuData={updateMenuData}
                      updateCategoryOptions={updateCategoryOptions}
                      triggerOptionLoadingState={triggerOptionLoadingState}
                      updateFormListingWithAttributes={updateFormListingWithAttributes}
                      listings={listings}
                      i13n={i13n}
                      changeReduxFromListings={changeReduxFromListings}
                    />
                  )}
                </TransitionDropItem>
              )
            })}
          </TransitionDropListContainer>
        )
      }}
    </FormValueSelector>
  )
}

export default AttributeRows
